import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Alert, Chip, Button,
  Skeleton, useTheme, alpha, Tooltip, IconButton,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  PersonAddAlt1 as PersonAddAlt1Icon,
  EventRepeat as EventRepeatIcon,
  AccessTime as AccessTimeIcon,
  ReceiptLong as ReceiptLongIcon,
  EventAvailable as EventAvailableIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell,
  Legend, Tooltip as RechartsTooltip,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';
import PainelNaoConfigurado from '../../components/saude/PainelNaoConfigurado';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import CircularScore from '../../components/saude/CircularScore';

// ─── Tipos ───

interface Totais {
  total: number;
  primeiras: number;
  retornos: number;
  pct_primeiras: number;
  pct_retornos: number;
  tempo_medio_min: number;
  com_prescricao: number;
  pct_prescricoes: number;
  com_retorno_agendado: number;
  pct_retorno_agendado: number;
}

interface PorCategoria {
  categoria: 'primeira' | 'retorno';
  total: number;
  tempo_medio_min: number;
  com_prescricao: number;
  pct_prescricoes: number;
}

interface PorUnidade {
  codunidade: string;
  nome: string;
  total: number;
  primeiras: number;
  retornos: number;
  tempo_medio_min: number;
  pct_prescricoes: number;
}

interface PorMes {
  mes: string;
  total: number;
  primeiras: number;
  retornos: number;
  pct_prescricoes: number;
}

interface PrimeirosAtendimentos {
  totais: Totais;
  por_categoria: PorCategoria[];
  por_unidade: PorUnidade[];
  por_mes: PorMes[];
  periodo: { inicio: string; fim: string };
  gerado_em: string;
}

const REFRESH_MS = 60_000;
const COR_PRIM = '#1976d2'; // primeiras consultas
const COR_RET = '#ff9800'; // retornos
const COR_TEMPO = '#00838f';
const COR_PRESC = '#43a047';

const toISO = (d: Date): string => d.toISOString().slice(0, 10);
const num = (n?: number | null) => (n ?? 0).toLocaleString('pt-BR');
const pct = (n?: number | null) => `${(n ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
const minutos = (n?: number | null) => `${(n ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} min`;

/** Periodo default: ultimos 30 dias. */
const hoje = new Date();
const trintaDiasAtras = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

// ─── KPI Card ───

const KpiCard: React.FC<{
  label: string; value: string; icon: React.ReactNode; color: string; loading: boolean;
}> = ({ label, value, icon, color, loading }) => {
  const theme = useTheme();
  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: 2, display: 'flex', alignItems: 'center',
          justifyContent: 'center', bgcolor: alpha(color, 0.12), color,
        }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          {loading
            ? <Skeleton width={72} height={36} />
            : <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{value}</Typography>}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{label}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Card de proporcao (gauge circular + percentual preciso) ───

const ProporcaoCard: React.FC<{
  titulo: string; valor: number; quantidade: number; total: number; cor: string; icon: React.ReactNode; loading: boolean;
}> = ({ titulo, valor, quantidade, total, cor, icon, loading }) => {
  const theme = useTheme();
  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
        {loading
          ? <Skeleton variant="circular" width={96} height={96} />
          : <CircularScore value={Math.round(valor)} color={cor} size={96} />}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: cor }}>
            {icon} {titulo}
          </Typography>
          {loading ? <Skeleton width={120} height={28} /> : (
            <>
              <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>{pct(valor)}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {num(quantidade)} de {num(total)} atendimentos
              </Typography>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Pagina ───

const SaudePrimeirosAtendimentos: React.FC = () => {
  const theme = useTheme();
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();

  const [data, setData] = useState<PrimeirosAtendimentos | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [naoConfigurado, setNaoConfigurado] = useState(false);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date | null>(null);
  const [segundos, setSegundos] = useState(REFRESH_MS / 1000);

  // Filtros — periodo default: ultimos 30 dias
  const [dataInicio, setDataInicio] = useState(toISO(trintaDiasAtras));
  const [dataFim, setDataFim] = useState(toISO(hoje));

  const fetchRef = useRef<() => void>(() => {});

  const fetchData = useCallback(async (silencioso = false) => {
    if (!municipioAtivo?.id) return;
    if (!silencioso) setLoading(true);
    setError(null);
    try {
      const result = await saudeService.getPrimeirosAtendimentos(municipioAtivo.id, {
        unidade: unidadeSelecionada?.codunidade,
        inicio: dataInicio,
        fim: dataFim,
      });
      setNaoConfigurado(result?.configurado === false);
      setData(result);
      setUltimaAtualizacao(new Date());
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar os primeiros atendimentos.');
    } finally {
      setLoading(false);
      setSegundos(REFRESH_MS / 1000);
    }
  }, [municipioAtivo?.id, unidadeSelecionada?.codunidade, dataInicio, dataFim]);

  useEffect(() => { fetchRef.current = () => fetchData(true); });

  // Busca inicial / ao trocar filtros
  useEffect(() => { fetchData(false); }, [fetchData]);

  // Auto-refresh a cada 60s (silencioso) + contador regressivo
  useEffect(() => {
    if (!municipioAtivo?.id) return;
    const refresh = setInterval(() => fetchRef.current(), REFRESH_MS);
    const tick = setInterval(() => setSegundos((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => { clearInterval(refresh); clearInterval(tick); };
  }, [municipioAtivo?.id]);

  const handleDateChange = useCallback((di: string, df: string) => {
    setDataInicio(di);
    setDataFim(df);
  }, []);

  const totais = data?.totais;
  const porCategoria = data?.por_categoria || [];
  const porUnidade = data?.por_unidade || [];
  const porMes = data?.por_mes || [];

  const catPrimeira = porCategoria.find((c) => c.categoria === 'primeira');
  const catRetorno = porCategoria.find((c) => c.categoria === 'retorno');

  const UnidadeTooltip = useMemo(() => ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload as PorUnidade;
    return (
      <Box sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 1 }}>
        <Typography variant="caption" fontWeight={600} display="block">{d.nome}</Typography>
        <Typography variant="caption" display="block">Atendimentos: {num(d.total)}</Typography>
        <Typography variant="caption" display="block" sx={{ color: COR_PRIM }}>Primeiras consultas: {num(d.primeiras)}</Typography>
        <Typography variant="caption" display="block" sx={{ color: COR_RET }}>Retornos: {num(d.retornos)}</Typography>
        <Typography variant="caption" display="block" sx={{ color: COR_PRESC }}>Prescricoes: {pct(d.pct_prescricoes)}</Typography>
      </Box>
    );
  }, [theme.palette.divider]);

  if (!municipioAtivo?.id) {
    return <Box sx={{ p: 3 }}><Alert severity="info">Selecione um municipio para visualizar os primeiros atendimentos.</Alert></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Cabecalho */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonAddAlt1Icon color="primary" /> Primeiros Atendimentos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {unidadeSelecionada
              ? <>Unidade: <strong>{unidadeSelecionada.nome}</strong></>
              : 'Todas as unidades do municipio'}
            {' • Primeiras consultas e retornos, tempo medio e prescricoes'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {ultimaAtualizacao ? `Atualizado as ${ultimaAtualizacao.toLocaleTimeString('pt-BR')}` : 'Carregando...'}
            </Typography>
            <Typography variant="caption" color="text.disabled">Proxima atualizacao em {segundos}s</Typography>
          </Box>
          <Tooltip title="Atualizar agora">
            <span>
              <IconButton onClick={() => fetchData(false)} disabled={loading} color="primary"
                sx={{ border: `1px solid ${theme.palette.divider}` }}>
                <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Filtro de periodo */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
        <CardContent sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <DateRangeSelector dataInicio={dataInicio} dataFim={dataFim} onChange={handleDateChange} />
        </CardContent>
      </Card>

      {naoConfigurado && <PainelNaoConfigurado />}
      {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => fetchData(false)}>Tentar novamente</Button>}>{error}</Alert>}

      {/* Proporcao primeiras x retornos */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <ProporcaoCard titulo="Primeiras consultas" valor={totais?.pct_primeiras ?? 0}
            quantidade={totais?.primeiras ?? 0} total={totais?.total ?? 0}
            cor={COR_PRIM} icon={<PersonAddAlt1Icon fontSize="small" />} loading={loading} />
        </Grid>
        <Grid item xs={12} md={6}>
          <ProporcaoCard titulo="Retornos" valor={totais?.pct_retornos ?? 0}
            quantidade={totais?.retornos ?? 0} total={totais?.total ?? 0}
            cor={COR_RET} icon={<EventRepeatIcon fontSize="small" />} loading={loading} />
        </Grid>
      </Grid>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}><KpiCard label="Atendimentos no periodo" value={num(totais?.total)} icon={<GroupsIcon />} color={COR_TEMPO} loading={loading} /></Grid>
        <Grid item xs={6} md={3}><KpiCard label="Tempo medio de atendimento" value={minutos(totais?.tempo_medio_min)} icon={<AccessTimeIcon />} color="#5e35b1" loading={loading} /></Grid>
        <Grid item xs={6} md={3}><KpiCard label="Atendimentos com prescricao" value={pct(totais?.pct_prescricoes)} icon={<ReceiptLongIcon />} color={COR_PRESC} loading={loading} /></Grid>
        <Grid item xs={6} md={3}><KpiCard label="Com retorno agendado" value={pct(totais?.pct_retorno_agendado)} icon={<EventAvailableIcon />} color={COR_RET} loading={loading} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Primeiras x retornos por unidade */}
        <Grid item xs={12} lg={7}>
          <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Primeiras consultas x retornos por unidade</Typography>
              {loading ? <Skeleton variant="rectangular" height={340} /> : (porUnidade.length ? (
                <ResponsiveContainer width="100%" height={Math.max(340, porUnidade.length * 46)}>
                  <BarChart data={porUnidade} layout="vertical" margin={{ top: 4, right: 32, bottom: 4, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={alpha(theme.palette.text.primary, 0.08)} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="nome" width={160} tick={{ fontSize: 11 }} interval={0} />
                    <RechartsTooltip content={<UnidadeTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="primeiras" name="Primeiras consultas" stackId="a" fill={COR_PRIM} radius={[0, 0, 0, 0]} />
                    <Bar dataKey="retornos" name="Retornos" stackId="a" fill={COR_RET} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <Box sx={{ py: 6, textAlign: 'center' }}><Typography color="text.secondary">Sem atendimentos no periodo selecionado.</Typography></Box>)}
            </CardContent>
          </Card>
        </Grid>

        {/* Evolucao mensal */}
        <Grid item xs={12} lg={5}>
          <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Evolucao mensal</Typography>
              {loading ? <Skeleton variant="rectangular" height={340} /> : (porMes.length ? (
                <ResponsiveContainer width="100%" height={340}>
                  <LineChart data={porMes} margin={{ top: 8, right: 16, bottom: 4, left: -16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.08)} />
                    <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <RechartsTooltip formatter={(v: any) => num(Number(v))} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="primeiras" name="Primeiras consultas" stroke={COR_PRIM} strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="retornos" name="Retornos" stroke={COR_RET} strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <Box sx={{ py: 6, textAlign: 'center' }}><Typography color="text.secondary">Sem dados no periodo selecionado.</Typography></Box>)}
            </CardContent>
          </Card>
        </Grid>

        {/* Comparativo por tipo de atendimento */}
        <Grid item xs={12}>
          <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Tempo medio e prescricoes por tipo de atendimento</Typography>
              <Grid container spacing={2}>
                {([
                  { cat: catPrimeira, nome: 'Primeiras consultas', cor: COR_PRIM, icon: <PersonAddAlt1Icon fontSize="small" /> },
                  { cat: catRetorno, nome: 'Retornos', cor: COR_RET, icon: <EventRepeatIcon fontSize="small" /> },
                ]).map(({ cat, nome, cor, icon }) => (
                  <Grid item xs={12} md={6} key={nome}>
                    <Box sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: cor, mb: 1.5 }}>
                        {icon} {nome}
                      </Typography>
                      {loading ? <Skeleton height={64} /> : (
                        <Grid container spacing={1}>
                          <Grid item xs={4}>
                            <Typography variant="h6" fontWeight={700}>{num(cat?.total)}</Typography>
                            <Typography variant="caption" color="text.secondary">Atendimentos</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="h6" fontWeight={700}>{minutos(cat?.tempo_medio_min)}</Typography>
                            <Typography variant="caption" color="text.secondary">Tempo medio</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="h6" fontWeight={700}>{pct(cat?.pct_prescricoes)}</Typography>
                            <Typography variant="caption" color="text.secondary">Com prescricao</Typography>
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Chip size="small" variant="outlined" sx={{ mt: 2 }}
        label={`Periodo: ${data?.periodo?.inicio ?? dataInicio} a ${data?.periodo?.fim ?? dataFim} • Primeira consulta = paciente sem atendimento anterior; retorno = paciente ja atendido • Retorno agendado = conduta de continuidade do cuidado`} />
    </Box>
  );
};

export default SaudePrimeirosAtendimentos;
